import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { UserGroupIcon, XMarkIcon, MagnifyingGlassIcon, CheckIcon, PlusIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { api, InvestigationDetail } from "./api";

type Candidate = { id: string; name: string | null; email: string; badge: string | null; hasSeat: boolean; isMember: boolean };

interface Props {
  open: boolean;
  onClose: () => void;
  detail: InvestigationDetail;
  onChanged: () => Promise<void>;
}

const initials = (name: string | null, email: string) =>
  (name || email)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const MembersModal: React.FC<Props> = ({ open, onClose, detail, onChanged }) => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const isOwner = detail.role === "owner";

  const load = async () => {
    try {
      const { data } = await axios.get<Candidate[]>(`/api/investigations/${detail.id}/members/candidates`);
      setCandidates(data);
    } catch {
      setCandidates([]);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSearch("");
    load();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail.id]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates
      .filter((c) => !c.isMember)
      .filter((c) => !q || (c.name || "").toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || (c.badge || "").toLowerCase().includes(q))
      .sort((a, b) => Number(b.hasSeat) - Number(a.hasSeat) || (a.name || a.email).localeCompare(b.name || b.email));
  }, [candidates, search]);

  if (!open) return null;

  const add = async (c: Candidate) => {
    setBusyId(c.id);
    try {
      await axios.post(`/api/investigations/${detail.id}/members`, { userId: c.id });
      await onChanged();
      await load();
      toast.success(`${c.name || c.email} added`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not add");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (userId: string, label: string) => {
    setBusyId(userId);
    try {
      await api.removeMember(detail.id, userId);
      await onChanged();
      await load();
      toast.success(`${label} removed`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not remove");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-base-300">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <UserGroupIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Detectives on this investigation</h3>
            <p className="text-sm text-base-content/60 mt-0.5">Members can see everything here. AI features need an Investigations seat, assigned by your agency admin.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50 mb-2">Members · {detail.members.length}</p>
          <ul className="divide-y divide-base-300 rounded-xl border border-base-300 overflow-hidden">
            {detail.members.map((m) => (
              <li key={m.user_id} className="flex items-center gap-3 px-3 py-2.5 bg-base-100">
                <span className="size-8 shrink-0 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-semibold flex items-center justify-center">{initials(m.name, m.email)}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-base-content truncate">{m.name || m.email}</p>
                  {m.name && <p className="text-xs text-base-content/50 truncate">{m.email}</p>}
                </div>
                <span className={cn("text-[11px] px-1.5 py-0.5 rounded-full", m.role === "owner" ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "bg-base-200 text-base-content/60")}>{m.role}</span>
                {m.role !== "owner" && isOwner && (
                  <button className="text-xs text-base-content/50 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40" disabled={busyId === m.user_id} onClick={() => remove(m.user_id, m.name || m.email)}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        {isOwner && (
          <div className="px-6 pb-5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50 mb-2">Add from your agency</p>
            <div className="relative mb-2">
              <MagnifyingGlassIcon className="size-4 text-base-content/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or badge"
                className="w-full rounded-lg border border-gray-300 dark:border-base-300 bg-base-100 pl-9 pr-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <ul className="max-h-56 overflow-auto rounded-xl border border-base-300 divide-y divide-base-300">
              {available.length === 0 && (
                <li className="px-3 py-4 text-sm text-base-content/50 text-center">{candidates.length === 0 ? "No other officers in your agency yet." : search ? "No matches." : "Everyone in your agency is already on this investigation."}</li>
              )}
              {available.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2 bg-base-100 hover:bg-base-200/60">
                  <span className="size-8 shrink-0 rounded-full bg-base-200 text-base-content/70 text-xs font-semibold flex items-center justify-center">{initials(c.name, c.email)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-base-content truncate">
                      {c.name || c.email}
                      {c.badge && <span className="ml-1.5 text-xs text-base-content/40">#{c.badge}</span>}
                    </p>
                    <p className="text-xs text-base-content/50 truncate">
                      {c.name ? `${c.email} · ` : ""}
                      {c.hasSeat ? <span className="text-green-700 dark:text-green-400 inline-flex items-center gap-0.5"><CheckIcon className="size-3" /> has a seat</span> : <span className="text-amber-700 dark:text-amber-400">no seat yet</span>}
                    </p>
                  </div>
                  <Button size="xs" variant="secondary" onClick={() => add(c)} disabled={busyId === c.id} title={c.hasSeat ? "Add" : "Add (they'll need a seat to use AI features)"}>
                    <PlusIcon className="size-3.5" /> Add
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-6 py-3 bg-base-200/60 border-t border-base-300">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/40" onClick={onClose} />
    </dialog>
  );
};

export default MembersModal;
